import { ReactElement } from "react";
import { Button } from "primereact/button";
import moment from "moment";

type Props = {
    formData: Record<string, any>;
    handleNextStep: () => void;
    handlePreviousStep: () => void;
}

function Preview(props: Props): ReactElement {
    const {
        handleNextStep,
        handlePreviousStep,
        formData,
    } = props;

    const formatPrice = (amount: number, isCurrency = false) => {

        if(isCurrency) {
            return Number(amount).toLocaleString("en-US");
        }
        return new Intl.NumberFormat("en-US", {
            style: 'currency',
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 10,
        }).format(amount);
    };

    return (
        <div className="w-full flex justify-content-center">
            <div className="w-7 mt-4 p-6 pb-0 bg-white relative">
                <div className="border-top-3 text-color text-base font-medium relative">
                    <header className="flex overflow-hidden border-bottom-2">
                        <div className="p-3 capitalize flex-none border-right-1">
                            <div className="text-color text-center">
                                form <span className="text-2xl font-bold">15620</span>
                                <span className="text-base block">
                                    (April 2025)
                                </span>
                            </div>
                        </div>
                        <div className="p-2 flex-grow-1 flex align-items-center flex-column">
                            <div className="text-base font-medium mt-1">
                                Department of the Treasury - Internal Revenue Service
                            </div>
                            <h2 className="text-4xl font-bold">Section 83(b) Election</h2>
                        </div>
                        <div className="p-3 capitalize flex-none border-left-1">
                            <div className="text-base text-center font-normal">OMB Number</div>
                            <div className="text-base text-center font-medium">1545-0074</div>
                        </div>
                    </header>
                    <div className="py-2 border-bottom-1">
                        The undersigned taxpayer hereby elects, pursuant to § 83(b) of the Internal Revenue Code of 1986, as amended, to include in gross
                        income as compensation for services the excess (if any) of the fair market value of the property described below over the amount paid
                        for the property.
                    </div>
                    <section className="py-1">
                        <div className="border-bottom-1 pb-1">
                            1. The taxpayer’s name, taxpayer identification number (TIN), and address:
                        </div>
                        <div className="w-full flex border-bottom-1">
                            <div className="w-8 p-1 flex flex-column gap-1">
                                <div>
                                    Taxpayer’s name
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formData?.taxpayer_name}</div>
                            </div>
                            <div className="w-4 p-1 border-left-1 flex flex-column gap-1">
                                <div>
                                    Taxpayer’s TIN
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formData?.taxpayer_id}</div>
                            </div>
                        </div>
                    </section>
                    <section className="py-1 border-bottom-1">
                        <div className="w-full flex flex-column gap-1">
                            <div>
                                Address
                                <span className="font-normal font-italic text-base">(number and street)</span>
                                <span className="ml-1 text-red-300">*</span>
                            </div>
                            <div>{formData?.address}</div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="w-full flex">
                            <div className="w-3 p-1 -my-2 flex flex-column gap-1">
                                <div>
                                    City
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formData?.city}</div>
                            </div>
                            <div className="w-3 p-1 -my-2 border-left-1 flex flex-column gap-1">
                                <div>
                                    State or province
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formData?.state}</div>
                            </div>
                            <div className="w-3 p-1 -my-2 border-left-1 flex flex-column gap-1">
                                <div>
                                    ZIP or postal code
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formData?.zip_code}</div>
                            </div>
                            <div className="w-3 p-1 -my-2 border-left-1 flex flex-column gap-1">
                                <div>
                                    Country
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formData?.country}</div>
                            </div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="w-full flex flex-column gap-1">
                            <div>
                                2. The property subject to this election is <span className="font-italic">(describe property and quantity below)</span>
                                <span className="ml-1 text-red-300">*</span>
                            </div>
                            <div>{formData?.property_subject_election}</div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="w-full flex flex-column gap-1">
                            <div>
                                3. The date the property is transferred
                                <span className="ml-1 text-red-300">*</span>
                            </div>
                            <div>{moment(formData?.date_of_transfer).format('MMMM D, YYYY')}</div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="w-full flex flex-column gap-1">
                            <div>
                                4. Taxable year for which the election is being made
                                <span className="font-italic">(taxable year that includes the date the property is transferred as reported in Box 3)</span>
                                <span className="ml-1 text-red-300">*</span>
                            </div>
                            <div>{formData?.taxable_year}</div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="w-full flex flex-column gap-1">
                            <div>
                                5. The property is subject to the following restrictions
                                <span className="font-italic">(describe applicable restrictions below)</span>
                                <span className="ml-1 text-red-300">*</span>
                            </div>
                            <div>{formData?.property_subject_restrictions}</div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="text-color py-1">
                            6. The total fair market value of the property at the time of transfer is
                            <span className="ml-1 text-red-300">*</span>
                        </div>
                        <div className="w-full flex">
                            <div className="w-4 flex flex-column gap-1">
                                <div>
                                    a. Value per item
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formatPrice(formData?.price_per_share)}</div>
                            </div>
                            <div className="w-4 flex flex-column gap-1">
                                <div>
                                    b. Quantity
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formatPrice(formData?.number_of_shares, true)}</div>
                            </div>
                            <div className="w-4 flex flex-column gap-1">
                                <div>
                                    c. Total fair market value
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formatPrice(formData?.FMV)}</div>
                            </div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="text-color py-1">
                            7. The total amount paid for the property is
                            <span className="ml-1 text-red-300">*</span>
                        </div>
                        <div className="w-full flex">
                            <div className="w-4 flex flex-column gap-1">
                                <div>
                                    a. Price paid per item
                                    <span className="ml-2 text-red-300">*</span>
                                </div>
                                <div>{formatPrice(formData?.price_paid)}</div>
                            </div>
                            <div className="w-4 flex flex-column gap-1">
                                <div>
                                    b. Quantity
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formatPrice(formData?.number_of_shares, true)}</div>
                            </div>
                            <div className="w-4 flex flex-column gap-1">
                                <div>
                                    c. Total price paid
                                    <span className="ml-1 text-red-300">*</span>
                                </div>
                                <div>{formatPrice(formData?.total_paid)}</div>
                            </div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="w-full flex flex-column gap-1">
                            <div>
                                8. The total amount to include in gross income for the taxable year is
                                <span className="font-italic ml-1">
                                    (the result of the amount reported in Box 6(с) minus the amount reported in Box 7(с))
                                </span>
                                <span className="ml-1 text-red-300">*</span>
                            </div>
                            <div>{formatPrice(formData?.income)}</div>
                        </div>
                    </section>
                    <section className="py-2">
                        <div className="border-bottom-1">
                            <div className="pb-1">
                                9. Name, TIN, and address of the person for whom the taxpayer is providing
                                services in connection with the transfer of property <span className="font-italic">(optional)</span>
                            </div>
                        </div>
                        <div className="w-full flex border-bottom-1">
                            <div className="w-8 p-1 flex flex-column gap-1">
                                <div>
                                    Name
                                </div>
                                <div style={{minHeight:"1.125rem"}}>{formData?.company_name}</div>
                            </div>
                            <div className="w-4 p-1 border-left-1 flex flex-column gap-1">
                                <div>
                                    TIN
                                </div>
                                <div style={{minHeight:"1.125rem"}}>{formData?.company_id}</div>
                            </div>
                        </div>
                    </section>
                    <section className="pb-1 border-bottom-1">
                        <div className="w-full flex flex-column gap-1">
                            <div>
                                Address
                                <span className="font-normal font-italic text-base">(number and street)</span>
                            </div>
                            <div style={{minHeight:"1.125rem"}}>{formData?.company_address}</div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <div className="w-full flex">
                            <div className="w-3 p-1 -my-2 flex flex-column gap-1">
                                <div>
                                    City
                                </div>
                                <div style={{minHeight:"1.125rem"}}>{formData?.company_city}</div>
                            </div>
                            <div className="w-3 p-1 -my-2 border-left-1 flex flex-column gap-1">
                                <div>
                                    State or province
                                </div>
                                <div style={{minHeight:"1.125rem"}}>{formData?.company_state}</div>
                            </div>
                            <div className="w-3 p-1 -my-2 border-left-1 flex flex-column gap-1">
                                <div>
                                    ZIP or postal code
                                </div>
                                <div style={{minHeight:"1.125rem"}}>{formData?.company_zip}</div>
                            </div>
                            <div className="w-3 p-1 -my-2 border-left-1 flex flex-column gap-1">
                                <div>
                                    Country
                                </div>
                                <div style={{minHeight:"1.125rem"}}>{formData?.company_country}</div>
                            </div>
                        </div>
                    </section>
                    <section className="py-2 border-bottom-1">
                        <p className="mb-2">
                            The undersigned taxpayer is the person performing the services in connection with which the property is transferred. The undersigned
                            taxpayer agrees to provide a copy of the election to (i) the person for whom the services are performed and (ii) the transferee of the
                            property, if the taxpayer and the transferee of the property are not the same person.
                        </p>
                        <p>
                            Under penalties of perjury, the undersigned taxpayer declares that, to the best of undersigned taxpayer’s knowledge and belief, the
                            information entered on this Form 15620 is true, correct, and complete.
                        </p>
                    </section>
                    <section className="py-2 border-bottom-1 flex">
                        <div className="w-10 pb-4 pt-1 -my-2">
                            Taxpayer signature
                        </div>
                        <div className="w-2 pl-1 -my-2 flex flex-column border-left-1">
                            <div className="pb-4 pt-1">Date signed</div>
                        </div>
                    </section>
                    <section className="py-1 flex align-items-baseline border-top-2">
                        <div className="flex-1">Catalog Number 95376D</div>
                        <div className="flex-1 text-center">
                            <a href="https://www.irs.gov/" target="_blank">www.irs.gov</a>
                        </div>
                        <div className="flex-1 text-right">
                            Form <span className="text-2xl font-bold">15620</span>
                            <span className="ml-1 text-sm text-center">
                                (Rev. 4-2025)
                            </span>
                        </div>
                    </section>
                    <footer className="py-4 sticky flex justify-content-end fixed bottom-0 bg-white gap-2" style={{ width: "100%" }}>
                        <Button label="Back" severity="secondary" onClick={handlePreviousStep} />
                        <Button label="Next" onClick={handleNextStep} />
                    </footer>
                </div>
            </div>
        </div>
    )
}

export default Preview;